import { compact, map } from 'es-toolkit/compat';

import {
  Annotation,
  AnnotationAssignment,
  AnnotationAssignmentUpdate,
  AnnotationClassRemovalOperation,
  TodoDefinition,
} from 'interfaces/annotation';
import { StainingMethod } from 'interfaces/stainType';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { CasesParams } from 'utils/useCasesParams/types';

export interface GetAnnotationAssignmentsParams extends CasesParams {
  slideStainType?: string;
  stainingMethod?: StainingMethod;
  skipProcedureParams?: boolean;
  skipSummary?: boolean;
  withLegacy?: boolean;
  fetchOnlySlidesOfStudy?: boolean;
  fetchAllSlidesOfGlobalAssignments?: boolean;
  datasetId?: string;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
}
export const ANNOTATIONS_BASE_QUERY_KEY = 'annotationAssignments';

export const getAnnotationAssignments = (data: GetAnnotationAssignmentsParams, signal: AbortSignal) => {
  return apiRequestHandlerPromise<{
    totalAnnotationAssignments: number;
    annotationAssignments: AnnotationAssignment[];
  }>({
    url: `annotation_assignments.get`,
    method: 'POST',
    data: JSON.stringify(data),
    signal,
  });
};

export const getAllAnnotationAssignments = async ({
  isGlobal,
  signal,
}: {
  isGlobal: boolean;
  signal?: AbortSignal;
}): Promise<AnnotationAssignment[]> => {
  return map(
    compact(
      await apiRequestHandlerPromise<AnnotationAssignment[]>({
        url: `annotation_assignments.get/all${isGlobal ? `?isGlobal=${isGlobal}` : ''}`,
        method: 'GET',
        signal,
      })
    ),
    (assignment) => ({ ...assignment, id: assignment?.annotationAssignmentId })
  );
};

export const getUnarchivedAnnotationAssignments = async (): Promise<AnnotationAssignment[]> => {
  return map(
    compact(
      await apiRequestHandlerPromise<AnnotationAssignment[]>({
        url: `annotation_assignments.get/all_unarchived`,
        method: 'GET',
      })
    ),
    (assignment) => ({
      ...assignment,
      id: assignment.annotationAssignmentId,
    })
  );
};

export const getTodoTypes = async ({ signal }: { signal?: AbortSignal }): Promise<TodoDefinition[]> => {
  return apiRequestHandlerPromise<TodoDefinition[]>({
    url: 'todo_types.get',
    method: 'GET',
    signal,
  });
};

export const getSlideAnnotationAssignmentHistory = async ({
  slideId,
  annotationAssignmentId,
  signal,
}: {
  slideId: string;
  annotationAssignmentId: number;
  signal?: AbortSignal;
}): Promise<Annotation['history']> => {
  return apiRequestHandlerPromise<Annotation['history']>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/history.get`,
    method: 'GET',
    signal,
  });
};

export const createAnnotationAssignment = async (data: AnnotationAssignmentUpdate & CasesParams) => {
  return apiRequestHandlerPromise<AnnotationAssignment>({
    url: 'annotation_assignments.create',
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const editAnnotationAssignment = async ({
  annotationAssignmentId,
  data,
}: {
  annotationAssignmentId: number;
  data: Partial<AnnotationAssignmentUpdate> &
    Partial<CasesParams & { classRemovalOperations?: AnnotationClassRemovalOperation[] }>;
}) => {
  return apiRequestHandlerPromise<AnnotationAssignment>({
    url: `annotation_assignments.update/${annotationAssignmentId}`,
    method: 'PUT',
    data: JSON.stringify(data),
  });
};

export interface DeleteDuplicateAnnotationAssignmentClassesResponse {
  removedDuplicateCount: number;
  cleanedClassNames: string[];
}

export const deleteDuplicateAnnotationAssignmentClasses = async ({
  annotationAssignmentId,
  classNames,
}: {
  annotationAssignmentId: number;
  classNames: string[];
}) =>
  apiRequestHandlerPromise<DeleteDuplicateAnnotationAssignmentClassesResponse>({
    url: `annotation_assignments/${annotationAssignmentId}/duplicate_classes.delete`,
    method: 'POST',
    data: JSON.stringify({ classNames }),
  });

export type AnnotationAssignmentBulkTagAction = 'add' | 'remove' | 'clear' | 'override';

export const normalizeAnnotationAssignmentTags = (tags: string[] = []) => {
  const seenTags = new Set<string>();
  return tags.reduce<string[]>((normalizedTags, tag) => {
    const normalizedTag = tag.trim();
    if (normalizedTag && !seenTags.has(normalizedTag)) {
      seenTags.add(normalizedTag);
      normalizedTags.push(normalizedTag);
    }

    return normalizedTags;
  }, []);
};

export const bulkEditAnnotationAssignment = async (data: {
  annotationAssignmentIdsToExclude?: number[];
  annotationAssignmentIdsToInclude?: number[];
  tagAction: AnnotationAssignmentBulkTagAction;
  tags: AnnotationAssignmentUpdate['tags'];
}) => {
  return apiRequestHandlerPromise({
    url: `annotation_assignments.update`,
    method: 'PUT',
    data: JSON.stringify(data),
  });
};

export const addSlidesToAnnotationAssignment = async ({
  annotationAssignmentId,
  casesParams,
}: {
  annotationAssignmentId: number;
  casesParams: CasesParams;
}) => {
  return apiRequestHandlerPromise<void>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/draft.create`,
    method: 'POST',
    data: JSON.stringify(casesParams),
  });
};

export const getAnnotationAssignmentsTagOptions = async (): Promise<string[]> => {
  return apiRequestHandlerPromise<string[]>({
    url: 'annotation_assignments/tags.get',
    method: 'GET',
  });
};

export interface ActivityTimelinePointByGroup {
  changes: number;
  slides: number;
  annotations: number;
}

export interface ActivityTimelineSnapshot {
  totalSlides: number;
  totalAnnotations: number;
  byWorkingState: Record<string, number>;
  byTagger: Record<string, number>;
  byClass: Record<string, number>;
}

export interface ActivityTimelinePoint {
  date: string;
  totalChanges: number;
  totalSlides: number;
  totalAnnotations: number;
  byTagger: Record<string, ActivityTimelinePointByGroup>;
  byWorkingState: Record<string, ActivityTimelinePointByGroup>;
  byClass: Record<string, number>;
  snapshot: ActivityTimelineSnapshot;
}

export interface ActivityTimelineResponse {
  points: ActivityTimelinePoint[];
}

export const getAnnotationAssignmentActivityTimeline = async ({
  annotationAssignmentId,
  signal,
}: {
  annotationAssignmentId: number;
  signal?: AbortSignal;
}): Promise<ActivityTimelineResponse> => {
  return apiRequestHandlerPromise<ActivityTimelineResponse>({
    url: `annotation_assignments/${annotationAssignmentId}/activity_timeline`,
    method: 'GET',
    signal,
  });
};
